/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import usePreciseTimer from '@hooks/usePreciseTimer';
import { noOp } from '@constants/empty';
import H5 from '@base/H5/H5';

const StyledCountdown = styled.div`
  display: flex;
`;

const TimerText = styled(H5)`
  line-height: 20px;
  color: ${({ color }) => color};
`;

const zeroPad = (number) => {
  return `0${number}`.slice(-2);
};

const Timer = ({
  remainingSeconds,
  onExpire,
  color,
  threshold,
  onThresholdReached,
  onTimerStarted
}) => {
  const [expired, setExpired] = useState(false);
  const [isTimerStarted, setIsTimerStarted] = useState(false);
  const [expiryTime, setExpiryTime] = useState(remainingSeconds);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [timerColor, setTimerColor] = useState(color);
  const reset = () => {
    setSeconds(0);
    setMinutes(0);
    setHours(0);
  };

  useEffect(() => {
    reset();
    setExpiryTime(remainingSeconds);
  }, [remainingSeconds]);

  const calculateExpiryDate = useCallback(
    (innerTimeDelay) => {
      if (threshold > expiryTime) {
        onThresholdReached();
        setTimerColor('var(--color-i-red)');
      }

      const distance = expiryTime - innerTimeDelay;
      const temphours = Math.floor(distance / 3600);
      const tempminutes = Math.floor((distance % 3600) / 60);
      const tempseconds = Math.floor((distance % 3600) % 60);

      if (distance < 0) {
        reset();
        onExpire();
        setExpired(true);
      } else {
        setSeconds(tempseconds);
        setMinutes(tempminutes);
        setHours(temphours);
        setExpiryTime(distance);
      }
      setIsTimerStarted(true);
      onTimerStarted();
    },
    [expiryTime, onExpire, threshold]
  );

  usePreciseTimer(calculateExpiryDate, 1000, !expired);

  return (
    <>
      {isTimerStarted && (
        <StyledCountdown>
          {hours > 0 ? (
            <TimerText color={timerColor}>{`${zeroPad(hours)} :`}</TimerText>
          ) : null}
          <TimerText color={timerColor}>
            &nbsp;
            {`${zeroPad(minutes)} :`}
          </TimerText>
          <TimerText color={timerColor}>
            &nbsp;
            {zeroPad(seconds)}
          </TimerText>
        </StyledCountdown>
      )}
    </>
  );
};

export default Timer;

Timer.propTypes = {
  remainingSeconds: PropTypes.number, // in seconds
  color: PropTypes.string,
  onExpire: PropTypes.func,
  threshold: PropTypes.number, // in seconds
  onThresholdReached: PropTypes.func,
  onTimerStarted: PropTypes.func
};

Timer.defaultProps = {
  remainingSeconds: 0,
  color: 'var(--color-text-primary)',
  onExpire: noOp,
  threshold: 0,
  onThresholdReached: noOp,
  onTimerStarted: noOp
};
